/**
 * Adjuster Analysis Section for Accountant Report View
 * AI-powered forensic analysis from the ADJUSTER/CARRIER perspective
 * 
 * Purpose: Help insurance adjusters identify fraud indicators, 
 * claim exaggeration, and opportunities to reduce claim payouts
 * 
 * Features:
 * - Executive summary with confidence level
 * - But-for revenue analysis with trend/seasonality
 * - Critical findings and fraud indicators
 * - Data quality assessment
 * - Cross-validation checks
 * - Actionable recommendations
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Shield,
  BarChart3,
  FileSearch,
  DollarSign
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ForensicAnalysisProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string };
  formatCurrency: (amount?: number) => string;
}

interface Finding {
  type?: string;
  title: string;
  description: string;
  severity?: string;
  evidence?: string;
  implication?: string;
  financial_impact?: number;
  recommendation?: string;
}

interface Opportunity {
  type?: string;
  title: string;
  description: string;
  financial_impact?: number;
  potential_value?: number;
  monthly_detail?: Array<{ period: string; recovery_pct: number; shortfall: number }>;
  recommendation?: string;
}

interface ButForMonth {
  period: string;
  but_for_revenue: number;
  actual_revenue: number;
  revenue_loss: number;
  seasonal_factor: number;
  recovery_pct: number;
}

interface ForensicAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  claim_amount?: number;
  executive_summary?: string;
  claim_validity?: string;
  confidence_level?: number;
  but_for_analysis?: {
    status: string;
    pre_incident_months?: number;
    post_incident_months?: number;
    avg_monthly_revenue?: number;
    trend_factor_monthly?: number;
    seasonality_indices?: Record<string, number>;
    total_but_for_revenue?: number;
    total_actual_revenue?: number;
    total_revenue_loss?: number;
    monthly_breakdown?: ButForMonth[];
  };
  critical_findings?: Finding[];
  opportunities?: Opportunity[];
  data_quality?: {
    score: number;
    grade: string;
    months_available: number;
    issues: string[];
    reliability: string;
  };
  cross_validation?: {
    cogs_ratio_avg?: number;
    payroll_avg?: number;
    issues?: Finding[];
  };
  pattern_analysis?: {
    benford?: { status?: string; deviation?: number; issue?: boolean };
    seasonality?: {
      peak_month: number;
      peak_index: number;
      trough_month: number;
      trough_index: number;
      pattern: string;
    };
  };
  recommendations?: string[];
  financial_impact?: {
    potential_reduction: number;
    potential_increase: number;
    net_adjustment: number;
  };
  error?: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function ForensicAnalysisSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: ForensicAnalysisProps) {
  const [analysis, setAnalysis] = useState<ForensicAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    findings: true,
    opportunities: true,
    butFor: false,
    dataQuality: false,
    patterns: false
  });

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/forensic/analyze/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: getAuthHeader() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Forensic analysis failed:', err);
      setError(err.response?.data?.detail || 'Failed to load forensic analysis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getValidityColor = (validity?: string) => {
    switch (validity) {
      case 'VALIDATED': return 'bg-green-100 text-green-800 border-green-300';
      case 'LOW_RISK': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'MODERATE_RISK': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'HIGH_RISK': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getSeverityColor = (severity?: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-50 border-red-300 text-red-900';
      case 'high': return 'bg-orange-50 border-orange-300 text-orange-900';
      case 'medium': return 'bg-amber-50 border-amber-300 text-amber-900';
      case 'low': return 'bg-blue-50 border-blue-300 text-blue-900';
      default: return 'bg-gray-50 border-gray-300 text-gray-900';
    }
  };

  const getSeverityIcon = (severity?: string) => {
    switch (severity) {
      case 'critical': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'high': return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'medium': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      default: return <CheckCircle2 className="w-5 h-5 text-blue-600" />;
    }
  };

  const getReliabilityBadge = (reliability?: string) => {
    switch (reliability) {
      case 'HIGH': return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">HIGH</span>;
      case 'MEDIUM': return <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-semibold">MEDIUM</span>;
      default: return <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-semibold">LOW</span>;
    }
  };

  if (loading && !analysis) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <FileSearch className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">Adjuster Analysis</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Running forensic analysis...</p>
            <p className="text-sm text-gray-500 mt-1">This may take a moment</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <FileSearch className="w-6 h-6 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-900">Adjuster Analysis</h2>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => fetchAnalysis(true)}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
          >
            Retry Analysis
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const { 
    executive_summary, 
    claim_validity, 
    confidence_level,
    but_for_analysis,
    critical_findings,
    opportunities,
    data_quality,
    pattern_analysis,
    recommendations,
    financial_impact,
    cached,
    analyzed_at
  } = analysis;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-testid="adjuster-analysis-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <FileSearch className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Adjuster Analysis</h2>
            <p className="text-sm text-gray-500">
              {cached ? '(Cached)' : 'Fresh analysis'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Re-analyze
        </button>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 mb-6 border border-indigo-100">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Executive Summary</h3>
            <p className="text-gray-700">{executive_summary}</p>
          </div>
          <div className="flex flex-col items-end gap-2 ml-4">
            <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border ${getValidityColor(claim_validity)}`}>
              {claim_validity?.replace('_', ' ')}
            </span>
            {confidence_level !== undefined && (
              <span className="text-sm text-gray-600">
                {(confidence_level * 100).toFixed(0)}% confidence
              </span>
            )}
          </div>
        </div>

        {/* Financial Impact Summary */}
        {financial_impact && (financial_impact.potential_reduction > 0 || financial_impact.potential_increase > 0) && (
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-indigo-200">
            <div className="text-center">
              <p className="text-sm text-gray-600">Potential Reduction</p>
              <p className="text-lg font-bold text-red-600">
                {formatCurrency(financial_impact.potential_reduction)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Potential Increase</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(financial_impact.potential_increase)}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-600">Net Adjustment</p>
              <p className={`text-lg font-bold ${financial_impact.net_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {financial_impact.net_adjustment >= 0 ? '+' : ''}{formatCurrency(financial_impact.net_adjustment)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Critical Findings */}
      {critical_findings && critical_findings.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('findings')}
            className="w-full flex items-center justify-between p-4 bg-red-50 rounded-lg hover:bg-red-100 transition"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-semibold text-red-900">
                Critical Findings ({critical_findings.length})
              </span>
            </div>
            {expandedSections.findings ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.findings && (
            <div className="mt-3 space-y-3">
              {critical_findings.map((finding, idx) => (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border-l-4 ${getSeverityColor(finding.severity)}`}
                >
                  <div className="flex items-start gap-3">
                    {getSeverityIcon(finding.severity)}
                    <div className="flex-1">
                      <h4 className="font-semibold">{finding.title}</h4>
                      <p className="text-sm mt-1">{finding.description}</p>
                      {finding.evidence && (
                        <p className="text-sm mt-2 text-gray-600">
                          <strong>Evidence:</strong> {finding.evidence}
                        </p>
                      )}
                      {finding.financial_impact !== undefined && finding.financial_impact > 0 && (
                        <p className="text-sm mt-1 font-semibold text-red-700">
                          Impact: {formatCurrency(finding.financial_impact)}
                        </p>
                      )}
                      {finding.recommendation && (
                        <p className="text-sm mt-2 text-indigo-700">
                          <strong>Recommendation:</strong> {finding.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Opportunities */}
      {opportunities && opportunities.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('opportunities')}
            className="w-full flex items-center justify-between p-4 bg-green-50 rounded-lg hover:bg-green-100 transition"
          >
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <span className="font-semibold text-green-900">
                Opportunities to Increase Claim ({opportunities.length})
              </span>
            </div>
            {expandedSections.opportunities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.opportunities && (
            <div className="mt-3 space-y-3">
              {opportunities.map((opp, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-lg bg-green-50 border border-green-200"
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900">{opp.title}</h4>
                      <p className="text-sm mt-1 text-green-800">{opp.description}</p>
                      {(opp.financial_impact || opp.potential_value) && (
                        <p className="text-sm mt-2 font-bold text-green-700">
                          Potential Value: +{formatCurrency(opp.financial_impact || opp.potential_value || 0)}
                        </p>
                      )}
                      {opp.monthly_detail && opp.monthly_detail.length > 0 && (
                        <div className="mt-3 bg-white rounded p-3">
                          <p className="text-xs font-semibold text-gray-600 mb-2">Monthly Recovery Detail</p>
                          <div className="space-y-1">
                            {opp.monthly_detail.map((m, i) => (
                              <div key={i} className="flex justify-between text-xs">
                                <span>{m.period}</span>
                                <span className="text-amber-600">{m.recovery_pct}% recovered</span>
                                <span className="text-red-600">-{formatCurrency(m.shortfall)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {opp.recommendation && (
                        <p className="text-sm mt-2 text-indigo-700">
                          <strong>Action:</strong> {opp.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* But-For Analysis */}
      {but_for_analysis && but_for_analysis.status === 'calculated' && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('butFor')}
            className="w-full flex items-center justify-between p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">
                But-For Revenue Analysis
              </span>
            </div>
            {expandedSections.butFor ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.butFor && (
            <div className="mt-3 bg-blue-50 rounded-lg p-4 border border-blue-200">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Pre-Incident Avg</p>
                  <p className="text-lg font-bold text-blue-700">
                    {formatCurrency(but_for_analysis.avg_monthly_revenue)}/mo
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Trend Factor</p>
                  <p className={`text-lg font-bold ${(but_for_analysis.trend_factor_monthly || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {(but_for_analysis.trend_factor_monthly || 0) >= 0 ? '+' : ''}{but_for_analysis.trend_factor_monthly}%/mo
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total But-For</p>
                  <p className="text-lg font-bold text-blue-700">
                    {formatCurrency(but_for_analysis.total_but_for_revenue)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Revenue Loss</p>
                  <p className="text-lg font-bold text-red-700">
                    {formatCurrency(but_for_analysis.total_revenue_loss)}
                  </p>
                </div>
              </div>

              {/* Seasonality Indices */}
              {but_for_analysis.seasonality_indices && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Seasonality Indices</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(but_for_analysis.seasonality_indices).map(([month, index]) => (
                      <div key={month} className="bg-white px-2 py-1 rounded text-xs">
                        <span className="text-gray-500">{MONTH_NAMES[parseInt(month) - 1]}:</span>
                        <span className={`ml-1 font-semibold ${index > 1.1 ? 'text-green-600' : index < 0.9 ? 'text-red-600' : 'text-gray-700'}`}>
                          {(index as number).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Breakdown */}
              {but_for_analysis.monthly_breakdown && but_for_analysis.monthly_breakdown.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Post-Incident Monthly Breakdown</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm bg-white rounded">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left">Period</th>
                          <th className="px-3 py-2 text-right">But-For</th>
                          <th className="px-3 py-2 text-right">Actual</th>
                          <th className="px-3 py-2 text-right">Loss</th>
                          <th className="px-3 py-2 text-right">Recovery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {but_for_analysis.monthly_breakdown.map((m, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-medium">{m.period}</td>
                            <td className="px-3 py-2 text-right text-blue-600">{formatCurrency(m.but_for_revenue)}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(m.actual_revenue)}</td>
                            <td className="px-3 py-2 text-right text-red-600">{formatCurrency(m.revenue_loss)}</td>
                            <td className="px-3 py-2 text-right">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                m.recovery_pct >= 90 ? 'bg-green-100 text-green-800' :
                                m.recovery_pct >= 70 ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {m.recovery_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Data Quality */}
      {data_quality && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('dataQuality')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-gray-600" />
              <span className="font-semibold text-gray-900">
                Data Quality Assessment: {data_quality.grade}
              </span>
              {getReliabilityBadge(data_quality.reliability)}
            </div>
            {expandedSections.dataQuality ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.dataQuality && (
            <div className="mt-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{data_quality.grade}</p>
                  <p className="text-xs text-gray-500">Grade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{data_quality.score}</p>
                  <p className="text-xs text-gray-500">Score</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{data_quality.months_available}</p>
                  <p className="text-xs text-gray-500">Months</p>
                </div>
              </div>
              
              {data_quality.issues && data_quality.issues.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Issues Found</p>
                  <ul className="space-y-1">
                    {data_quality.issues.map((issue, idx) => (
                      <li key={idx} className="text-sm text-amber-700 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pattern Analysis */}
      {pattern_analysis && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('patterns')}
            className="w-full flex items-center justify-between p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition"
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-purple-900">Pattern Analysis</span>
            </div>
            {expandedSections.patterns ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {expandedSections.patterns && (
            <div className="mt-3 bg-purple-50 rounded-lg p-4 border border-purple-200">
              <div className="grid grid-cols-2 gap-4">
                {/* Benford's Law */}
                {pattern_analysis.benford && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Benford's Law Check</p>
                    {pattern_analysis.benford.status === 'passed' ? (
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm">Passed ({pattern_analysis.benford.deviation}% deviation)</span>
                      </div>
                    ) : pattern_analysis.benford.issue ? (
                      <div className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">{pattern_analysis.benford.deviation}% deviation - investigate</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">Insufficient data</span>
                    )}
                  </div>
                )}

                {/* Seasonality */}
                {pattern_analysis.seasonality && (
                  <div className="bg-white rounded-lg p-3">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Seasonality Pattern</p>
                    <p className="text-sm text-gray-600">
                      <span className="font-semibold capitalize">{pattern_analysis.seasonality.pattern}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Peak: {MONTH_NAMES[pattern_analysis.seasonality.peak_month - 1]} ({pattern_analysis.seasonality.peak_index.toFixed(2)}x) •
                      Low: {MONTH_NAMES[pattern_analysis.seasonality.trough_month - 1]} ({pattern_analysis.seasonality.trough_index.toFixed(2)}x)
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
            <Lightbulb className="w-5 h-5" />
            Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-indigo-800">
                <span className="text-indigo-500 mt-1">→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
